import { Col, Input, Row } from "antd";
import { useStoreOfferMutation } from "api/TenantSlug/Offer/hooks";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm";
import { AdvertiserSelectSearchable } from "components/UI/Selects/AdvertiserSelectSearchable/AdvertiserSelectSearchable.tsx";
import { CurrencySelectSearchable } from "components/UI/Selects/CurrencySelectSearchable/CurrencySelectSearchable.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useUser } from "hooks/useUser";
import { TOAST_TYPES } from "localConstants";
import { FC } from "react";
import { FormItem } from "react-hook-form-antd";
import { toast } from "react-toastify";
import { OfferAccess, OfferStatus } from "types/enums";
import { showCustomToast } from "utils/toast";

import { AddOfferFormData, schema } from "./formSchema";

import StoreOfferRequest = App.Http.Requests.Offer.StoreOfferRequest;

const AddOfferFormContent: FC = () => {
    const { t: tFields } = usePaldockTranslation("forms.general-fields");

    const generalFormUtils = useGeneralForm<AddOfferFormData>();
    const { control, fieldNames } = generalFormUtils;

    return (
        <Row gutter={16} align="middle" wrap>
            <Col span={8}>
                <FormItem control={control} name={fieldNames.title.name} label={tFields("name.label")} required>
                    <Input placeholder={tFields("name.placeholder")} />
                </FormItem>
            </Col>
            <Col span={8}>
                <FormItem control={control} name={fieldNames.advertiser.name} label={tFields("advertiser.label")} required>
                    <AdvertiserSelectSearchable />
                </FormItem>
            </Col>
            <Col span={8}>
                <FormItem control={control} name={fieldNames.currency_id.name} label={tFields("currency.label")} required>
                    <CurrencySelectSearchable />
                </FormItem>
            </Col>
        </Row>
    );
};

interface AddOfferFormProps {
    formId: string;
    onFormSubmit?: () => void;
}

export const AddOfferForm: FC<AddOfferFormProps> = ({ formId, onFormSubmit }) => {
    const { t } = usePaldockTranslation("forms.add-offer");
    const { t: tC } = usePaldockTranslation("common");
    const defaultValues = {
        title: "",
        advertiser: "",
        currency_id: null,
    };
    const userUtils = useUser();
    const storeOfferMutation = useStoreOfferMutation();
    const generalFormUtils = useGeneralForm<AddOfferFormData>();

    const onSubmit = (values: AddOfferFormData) => {
        const data: StoreOfferRequest = {
            title: values.title,
            visible_title: values.title,
            description: null,
            status: OfferStatus.Disabled,
            access: OfferAccess.Private,
            enabled_from: null,
            enabled_to: null,
            cap_clicks: null,
            cap_leads: null,
            cap_payouts: null,
            advertiser_id: values.advertiser,
            currency_id: values.currency_id,
            filter: null,
        };

        storeOfferMutation.mutate(data, {
            onSuccess: (response) => {
                showCustomToast(
                    {
                        title: t("messages.success"),
                        buttons: [
                            {
                                label: tC("open"),
                                href: response.offer.id
                                    ? `/${userUtils.getActiveTenant()?.slug_name}/offer/edit/${response.offer.id}`
                                    : undefined,
                            },
                        ],
                    },
                    TOAST_TYPES.SUCCESS
                );
                onFormSubmit?.();
            },
            onError: (error) => {
                toast.error(`${error.message}`);
                generalFormUtils.handleErrorsFromErrorResponse(error);
            },
        });
    };

    return (
        <div className="max-w-xl">
            <GeneralFormProvider<AddOfferFormData>
                id={formId}
                schema={schema}
                defaultValues={defaultValues}
                onSubmit={onSubmit}
            >
                <AddOfferFormContent />
            </GeneralFormProvider>
        </div>
    );
};
